import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  ShoppingCart,
  Instagram,
  Youtube,
  Facebook,
  Twitter,
  Plus,
  Minus
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../services/AuthService";
import { motion, AnimatePresence } from "framer-motion";




export default function JazzHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showGalleryDropdown, setShowGalleryDropdown] = useState(false);
  const { cart, removeFromCart, increaseQty, decreaseQty } = useCart();
  const { user } = useAuth();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Robust cart count calculation
  // We sum quantities for merch, but treat music as 1 item in the counter if we want to show it exists
  // The user previously asked to remove the "counter" for digital, which we did by not showing qty controls
  // But if they want "total no of items", we should ensure something shows.
  const cartCount = Array.isArray(cart)
    ? cart.reduce((total, item) => {
      const isDigital = item?.category_type?.toLowerCase().includes("music");
      const qty = Number(item?.quantity) || 0;
      // Sum quantities for non-digital, add 1 for digital (since they can only have 1 anyway)
      return total + (isDigital ? 1 : qty);
    }, 0)
    : 0;

  const totalItemsInCart = Array.isArray(cart) ? cart.length : 0;
  const dropdownTimeoutRef = useRef(null);

  // setCartCount(prev => prev + 1);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen || isCartOpen) {
      document.body.classList.add("body-lock");
    } else {
      document.body.classList.remove("body-lock");
    }
    return () => {
      document.body.classList.remove("body-lock");
    };
  }, [isMenuOpen, isCartOpen]);

  const navItems = [
    { name: "Home", href: "/" },
    { name: "Biography", href: "/biography" },
    { name: "Discography", href: "/discography" },
    { name: "Events", href: "/events" },
    {
      name: "Gallery",
      href: "#gallery",
      hasDropdown: true,
      subItems: [
        { name: "Images", href: "/gallery/images" },
        { name: "Videos", href: "/gallery/videos" },
      ],
    },
    { name: "Shop", href: "/shop" },
    { name: "Press", href: "/press" },
    { name: "Contact", href: "/contact" },
  ];

  const socialLinks = [
    { name: "Instagram", icon: Instagram, href: "https://www.instagram.com/temidayo_balogun/" },
    { name: "YouTube", icon: Youtube, href: "https://www.youtube.com/@TemidayoBalogun" },
    { name: "Facebook", icon: Facebook, href: "https://www.facebook.com/temidayobalogunmusic/" },
    { name: "Twitter", icon: Twitter, href: "https://twitter.com" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 w-full max-w-full transition-[background-color,box-shadow,backdrop-filter] duration-300 ${isMenuOpen ? "z-[9999]" : "z-50"
          } ${isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-[0_1px_20px_rgba(0,0,0,0.08)]"
            : "bg-transparent"
          }`}
        style={{
          fontFamily:
            'Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          transform: "translateZ(0)",
          WebkitTransform: "translateZ(0)",
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
        }}
      >
        <div className="px-4 sm:px-6 lg:px-12 py-3 sm:py-4 lg:py-5 w-full max-w-full overflow-hidden">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between">
            {/* Logo - Left */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                {/* Musical note icon as logo */}
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center overflow-hidden">
                  <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun logo" className="w-full h-full object-contain scale-[1.8] transition-transform duration-200 group-hover:scale-[1.5]" />
                </div>
              </div>
            </Link>

            {/* Desktop Navigation - Right */}
            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => {
                    if (item.hasDropdown) {
                      if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
                      setShowGalleryDropdown(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (item.hasDropdown) {
                      dropdownTimeoutRef.current = setTimeout(() => {
                        setShowGalleryDropdown(false);
                      }, 150);
                    }
                  }}
                >
                  <NavLink
                    to={item.href}
                    end={item.href === "/"}
                    className={({ isActive }) => {
                      const isGallery = item.name === "Gallery";
                      const active = isGallery
                        ? location.pathname.startsWith('/gallery')
                        : isActive;

                      return `text-sm font-medium transition-colors duration-200 flex items-center gap-1 py-2 border-b-[4px] ${active ? "border-jazz-brown text-jazz-brown font-black" : "border-transparent text-black hover:text-jazz-brown"
                        }`;
                    }}
                    style={{ letterSpacing: "0.5px" }}
                  >
                    {item.name}
                    {item.hasDropdown && (
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-200 ${showGalleryDropdown ? "rotate-180" : ""}`}
                      />
                    )}
                  </NavLink>

                  {/* Dropdown Menu */}
                  {item.hasDropdown && showGalleryDropdown && (
                    <>
                      {/* Invisible bridge to prevent mouseLeave when moving between trigger and menu */}
                      <div className="absolute top-full left-0 w-full h-2 z-[49]" />
                      <div className="absolute top-[calc(100%-4px)] left-0 mt-1 bg-white border border-black/10 rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.12)] min-w-[160px] py-2 opacity-100 animate-fadeIn z-50">
                        {item.subItems.map((subItem) => (
                          <NavLink
                            key={subItem.name}
                            to={subItem.href}
                            className={({ isActive }) =>
                              `block px-4 py-2 text-sm transition-colors duration-150 border-l-[6px] ${isActive ? "border-jazz-brown text-jazz-brown bg-black/5 font-black" : "border-transparent text-black hover:bg-black/5 font-bold"
                              }`
                            }
                          >
                            {subItem.name}
                          </NavLink>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-6">
              <Link
                to={user ? "/dashboard" : "/login"}
                className="py-2.5 px-6 bg-black text-white font-bold text-[10px] tracking-[2px] uppercase rounded-full shadow-sm hover:bg-jazz-brown hover:shadow-md transform hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                {user ? "Dashboard" : "Sign In"}
              </Link>
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 group"
                aria-label="Shopping cart"
              >
                <ShoppingCart size={22} className="text-black group-hover:text-jazz-brown transition-colors" />
                {totalItemsInCart > 0 && (
                  <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg group-hover:bg-jazz-brown transition-colors z-[100]">
                    {cartCount > 0 ? cartCount : totalItemsInCart}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Actions - Cart + Menu */}
            <div className="lg:hidden flex items-center gap-3">
              {/* Cart Button - Mobile Only */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 touch-manipulation"
                aria-label="Shopping cart"
              >
                <ShoppingCart size={22} className="text-black" />
                {totalItemsInCart > 0 && (
                  <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center z-[100]">
                    {cartCount > 0 ? cartCount : totalItemsInCart}
                  </span>
                )}
              </button>

              {/* Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex flex-col items-center gap-1 p-2 touch-manipulation"
                aria-label="Toggle menu"
              >
                <span className="text-[9px] sm:text-[10px] font-medium text-black uppercase tracking-wider">
                  Menu
                </span>
                <div className="text-black hover:text-[#404040] transition-colors duration-200">
                  {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Off-Canvas Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm lg:hidden z-[9998]"
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 h-full w-[280px] bg-white shadow-[2px_0_30px_rgba(0,0,0,0.15)] lg:hidden overflow-y-auto z-[9999] flex flex-col"
            >
              <div className="p-6 flex flex-col h-full">
                {/* Header with Close button and Logo */}
                <div className="flex items-center justify-between mb-8">
                  {/* Close button - Left */}
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="text-black hover:text-[#404040] transition-colors duration-200 p-2 touch-manipulation"
                    aria-label="Close menu"
                  >
                    <X size={24} />
                  </button>

                  {/* Logo - Right */}
                  <Link
                    to="/"
                    className="flex items-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
                      <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun logo" className="w-full h-full object-contain scale-[1.8] transition-transform duration-200 group-hover:scale-[1.5]" />
                    </div>
                  </Link>
                </div>

                {/* Mobile nav items */}
                <nav className="flex flex-col gap-1 flex-1">
                  {navItems.map((item) => (
                    <div key={item.name}>
                      <NavLink
                        to={item.href}
                        end={item.href === "/"}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) => {
                          const isGallery = item.name === "Gallery";
                          const active = isGallery
                            ? location.pathname.startsWith('/gallery')
                            : isActive;

                          return `block text-base font-medium transition-colors duration-200 py-3 px-2 touch-manipulation border-l-[6px] ${active ? "text-jazz-brown border-jazz-brown bg-jazz-brown/10 font-black" : "text-black border-transparent hover:text-jazz-brown"
                            }`;
                        }}
                        style={{ letterSpacing: "0.5px" }}
                      >
                        {item.name}
                      </NavLink>
                      {item.hasDropdown && item.subItems && (
                        <div className="ml-4 mt-1 flex flex-col gap-1">
                          {item.subItems.map((subItem) => (
                            <NavLink
                              key={subItem.name}
                              to={subItem.href}
                              onClick={() => setIsMenuOpen(false)}
                              className={({ isActive }) =>
                                `block text-sm transition-colors duration-200 py-2 px-2 touch-manipulation ${isActive ? "text-jazz-brown font-semibold" : "text-black font-semibold hover:text-jazz-brown"
                                }`
                              }
                            >
                              {subItem.name}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>

                {/* User Actions - Mobile Menu */}
                <div className="mt-6 mb-2">
                  <Link
                    to={user ? "/dashboard" : "/login"}
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full text-center py-3.5 px-6 bg-black text-white font-bold text-[10px] tracking-[2px] uppercase rounded-xl hover:bg-jazz-brown shadow-md active:scale-[0.98] transition-all"
                  >
                    {user ? "Go to Dashboard" : "Sign In"}
                  </Link>
                </div>

                {/* Social Media Icons - Bottom of Mobile Menu */}
                <div className="border-t border-black/10 pt-6 mt-6">
                  <p className="text-xs font-semibold text-black uppercase tracking-wider mb-4">
                    Follow Me
                  </p>
                  <div className="flex items-center gap-4">
                    {socialLinks.map((social) => {
                      const Icon = social.icon;
                      return (
                        <Link
                          key={social.name}
                          to={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all duration-200 touch-manipulation"
                          aria-label={social.name}
                        >
                          <Icon size={18} />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Cart Drawer Overlay */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCartOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[10000]"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[10001] flex flex-col"
            >
              <div className="p-6 border-b border-black/5 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-serif italic">Your Cart</h2>
                  <p className="text-[10px] font-bold tracking-[2px] uppercase text-black/80">
                    {cartCount} {cartCount === 1 ? 'Item' : 'Items'}
                  </p>
                </div>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="p-2 hover:bg-black/5 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {cart.length > 0 ? (
                  cart.map((item) => (
                    <div key={item.id} className="flex gap-4 group">
                      <div className="w-20 h-20 bg-gray-50 rounded-xl overflow-hidden flex-shrink-0 border border-black/5">
                        <img src={item.image || item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="text-sm font-medium text-black truncate pr-4">{item.title}</h3>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-black/65 hover:text-red-500 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <p className="text-xs text-black/80 mb-3">{item.category}</p>
                        <div className="flex justify-between items-center">
                          {item.category_type === "Music Content" ? (
                            <div className="text-[10px] font-bold tracking-[1px] uppercase text-jazz-brown bg-jazz-brown/5 px-2 py-1 rounded">
                              Digital Download
                            </div>
                          ) : (
                            <div className="flex items-center border border-black/5 rounded-lg bg-gray-50">
                              <button onClick={() => decreaseQty(item.id)} className="p-1 hover:text-jazz-brown">
                                <Minus size={12} />
                              </button>
                              <span className="w-8 text-center text-xs font-bold">{item.quantity}</span>
                              <button onClick={() => increaseQty(item.id)} className="p-1 hover:text-jazz-brown">
                                <Plus size={12} />
                              </button>
                            </div>
                          )}
                          <span className="text-sm font-bold text-black font-serif">${((Number(item?.price) || 0) * (Number(item?.quantity) || 0)).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <ShoppingCart size={24} className="text-black/65" />
                    </div>
                    <p className="text-sm font-serif italic text-black font-medium">Your cart is empty</p>
                    <button
                      onClick={() => {
                        setIsCartOpen(false);
                        navigate("/shop");
                      }}
                      className="mt-6 text-xs font-bold tracking-[2px] uppercase text-jazz-brown hover:underline"
                    >
                      Continue Shopping
                    </button>
                  </div>
                )}
              </div>

              {cart.length > 0 && (
                <div className="p-6 bg-gray-50 border-t border-black/5 space-y-4">
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-xs font-bold tracking-[1px] uppercase text-black/80">Subtotal</span>
                    <span className="text-2xl font-bold text-black font-serif">
                      ${cart.reduce((acc, item) => {
                        const price = Number(item.price) || 0;
                        const qty = Number(item.quantity) || 0;
                        return acc + (price * qty);
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                  <Link
                    to="/checkout"
                    onClick={() => setIsCartOpen(false)}
                    className="w-full bg-black text-white py-4 rounded-xl text-xs font-bold tracking-[3px] uppercase hover:bg-jazz-brown transition-all duration-500 shadow-xl flex items-center justify-center"
                  >
                    Checkout Now
                  </Link>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style jsx="true">{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
}
